/* Hero slideshow. The slides live in a native scroll-snap track, so swiping and
   trackpad scrolling are handled by the browser; this drives the controls,
   autoplay and background video, and keeps the dots in step with the scroll. */
class LSHero {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-slides]');
    this.slides = Array.from(root.querySelectorAll('[data-slide]'));
    this.dots = Array.from(root.querySelectorAll('[data-dot]'));
    this.toggle = root.querySelector('[data-toggle]');
    this.index = 0;
    this.timer = null;
    this.paused = false;

    if (!this.track || this.slides.length === 0) return;

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.speed = (parseInt(root.dataset.autoplaySpeed, 10) || 7) * 1000;
    this.rotates = root.dataset.autoplay === 'true' && !this.reduced && this.slides.length > 1;

    if (this.slides.length < 2) {
      this.dots.forEach((dot) => (dot.hidden = true));
      // With one slide the toggle only still has a job if there is a video to stop.
      if (this.toggle && !this.hasVideo()) this.toggle.hidden = true;
    }

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        this.goTo(parseInt(dot.dataset.index, 10));
        this.restart();
      });
    });

    if (this.toggle) {
      this.toggle.addEventListener('click', () => this.setPaused(!this.paused));
    }

    this.track.addEventListener('scroll', () => {
      if (this.scrollSettle) clearTimeout(this.scrollSettle);
      this.scrollSettle = setTimeout(() => this.syncFromScroll(), 80);
    }, { passive: true });

    // Hovering or dragging holds the rotation but leaves the video alone — only
    // the explicit toggle stops playback.
    root.addEventListener('mouseenter', () => this.stop());
    root.addEventListener('mouseleave', () => this.restart());
    this.track.addEventListener('pointerdown', () => this.stop(), { passive: true });
    this.track.addEventListener('touchstart', () => this.stop(), { passive: true });
    this.track.addEventListener('touchend', () => this.restart(), { passive: true });

    root.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      this.goTo(this.index + (e.key === 'ArrowRight' ? 1 : -1));
      this.restart();
    });

    window.addEventListener('resize', () => this.syncFromScroll(), { passive: true });

    this.setActive(0);
    this.setPaused(false);
  }

  hasVideo() {
    return this.root.querySelector('video') !== null;
  }

  get step() {
    return this.track.clientWidth;
  }

  /* Only the slide on screen plays, and nothing plays while paused. */
  syncVideos() {
    this.slides.forEach((slide, i) => {
      slide.querySelectorAll('video').forEach((video) => {
        if (i === this.index && !this.paused) {
          const played = video.play();
          if (played && typeof played.catch === 'function') played.catch(() => {});
        } else {
          video.pause();
        }
      });
    });
  }

  syncFromScroll() {
    const i = Math.max(0, Math.min(Math.round(this.track.scrollLeft / this.step), this.slides.length - 1));
    if (i === this.index) return;
    this.setActive(i);
    this.restart();
  }

  setActive(index) {
    this.index = index;
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
      if (i === index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
    this.syncVideos();
  }

  goTo(index) {
    const next = (index + this.slides.length) % this.slides.length;
    this.track.scrollTo({ left: next * this.step, behavior: this.reduced ? 'auto' : 'smooth' });
    this.setActive(next);
  }

  start() {
    this.stop();
    if (!this.rotates || this.paused) return;
    this.root.dataset.paused = 'false';
    this.timer = setInterval(() => this.goTo(this.index + 1), this.speed);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.root.dataset.paused = 'true';
  }

  restart() {
    this.start();
  }

  /* The toggle governs both the rotation and the background video. */
  setPaused(paused) {
    this.paused = paused;
    if (this.toggle) {
      this.toggle.dataset.playing = String(!paused);
      this.toggle.setAttribute('aria-pressed', String(paused));
    }
    if (paused) {
      this.stop();
    } else {
      this.start();
    }
    this.syncVideos();
  }
}

function initLSHeroes(scope) {
  (scope || document).querySelectorAll('[data-ls-hero]').forEach((root) => {
    if (root.dataset.lsHeroReady === 'true') return;
    root.dataset.lsHeroReady = 'true';
    new LSHero(root);
  });
}

initLSHeroes();

// Theme editor re-renders a section's markup on change, so re-bind.
document.addEventListener('shopify:section:load', (event) => initLSHeroes(event.target));
